export const BUILDING_VIEW_BANDS = {
    BASIC: "Basic Details",
    MORE: "More Details",
    ASSESSMENT: "Assessment Details",
    ADDRESS: "Address"
};

export const BUILDING_VIEW_STATIC_BAND_NAMES = new Set(Object.values(BUILDING_VIEW_BANDS));

export const DYNAMIC_BAND_KEY_PREFIX = "__dynamic__:";

export const dynamicBandConfigKey = bandName => `${DYNAMIC_BAND_KEY_PREFIX}${bandName}`;

export const isDynamicBandConfigKey = bandKey =>
    typeof bandKey === "string" && bandKey.startsWith(DYNAMIC_BAND_KEY_PREFIX);

export const getDynamicBandDisplayName = bandKey =>
    isDynamicBandConfigKey(bandKey) ? bandKey.slice(DYNAMIC_BAND_KEY_PREFIX.length) : bandKey;

export const getBuildingViewFieldBandLabel = (bandKey, fieldConfig) => {
    if (!bandKey) return "Other";
    if (isDynamicBandConfigKey(bandKey)) {
        const display = getDynamicBandDisplayName(bandKey);
        if (BUILDING_VIEW_STATIC_BAND_NAMES.has(display)) {
            return `${display} (Dynamic Fields)`;
        }
        return display;
    }
    const sampleKey = (fieldConfig?.keys || []).find(
        k => fieldConfig?.config?.[k] && (fieldConfig.config[k].band === bandKey || fieldConfig.config[k].bandForm === bandKey)
    );
    return fieldConfig?.config?.[sampleKey]?.bandDisplay || bandKey;
};

export const BUILDING_VIEW_ALWAYS_VISIBLE_KEYS = new Set(["main_image", "gauge_chart"]);

const field = (key, label, band, isVisible = true, bandForm = band) => ({
    key,
    label,
    band,
    bandForm,
    isVisible
});

const STATIC_FIELDS = [
    field("code", "Building Code", BUILDING_VIEW_BANDS.BASIC),
    field("name", "Building Name", BUILDING_VIEW_BANDS.BASIC),
    field("main_building_type_id", "Building Type", BUILDING_VIEW_BANDS.BASIC),
    field("description", "Description", BUILDING_VIEW_BANDS.BASIC),
    field("site_id", "Site", BUILDING_VIEW_BANDS.BASIC),
    field("region_id", "Region", BUILDING_VIEW_BANDS.BASIC),
    field("area", "Area (Sq Ft)", BUILDING_VIEW_BANDS.BASIC),
    field("cost", "Cost", BUILDING_VIEW_BANDS.BASIC),
    field("crv", "CRV", BUILDING_VIEW_BANDS.BASIC),
    field("main_image", "Main Image", BUILDING_VIEW_BANDS.BASIC),

    field("year", "Year Built", BUILDING_VIEW_BANDS.MORE),
    field("original_age", "Original Age", BUILDING_VIEW_BANDS.MORE),
    field("major_renovation_year", "Year of Major Renovation", BUILDING_VIEW_BANDS.MORE),
    field("major_renovation_age", "Major Renovation Age", BUILDING_VIEW_BANDS.MORE),
    field("ownership_type", "Ownership Type", BUILDING_VIEW_BANDS.MORE),
    field("enterprise_index", "Enterprise Index", BUILDING_VIEW_BANDS.MORE),
    field("ownership", "Ownership", BUILDING_VIEW_BANDS.MORE),
    field("leases_count", "Total Leases", BUILDING_VIEW_BANDS.MORE),
    field("lease_end_date", "Lease End Date", BUILDING_VIEW_BANDS.MORE),
    field("manager", "Manager", BUILDING_VIEW_BANDS.MORE),
    field("senior_property_manager", "Senior Property Manager", BUILDING_VIEW_BANDS.MORE),
    field("use", "Primary Use", BUILDING_VIEW_BANDS.MORE),
    field("secondary_use", "Secondary Use", BUILDING_VIEW_BANDS.MORE),
    field("sector", "Sector", BUILDING_VIEW_BANDS.MORE),
    field("internal_group", "Internal Group", BUILDING_VIEW_BANDS.MORE),
    field("division", "Division", BUILDING_VIEW_BANDS.MORE),
    field("status", "Status", BUILDING_VIEW_BANDS.MORE),
    field("criticality", "Criticality", BUILDING_VIEW_BANDS.MORE),
    field("window_sf", "Window SF", BUILDING_VIEW_BANDS.MORE),
    field("climate_zone", "Climate Zone", BUILDING_VIEW_BANDS.MORE),
    field("sf_by_ton_standard", "Ton Standard/SF", BUILDING_VIEW_BANDS.MORE),
    field("sf_by_ton_actual", "Ton Actual/SF", BUILDING_VIEW_BANDS.MORE),
    field("hospital_name", "Parent Building", BUILDING_VIEW_BANDS.MORE),
    field("ministry", "Ministry", BUILDING_VIEW_BANDS.MORE),
    field("color_code", "Color", BUILDING_VIEW_BANDS.MORE),
    field("sort_order", "Export Sort Order", BUILDING_VIEW_BANDS.MORE),
    field("number", "Building Number", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("consultancy_id", "Consultancy", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("client_id", "Client", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("projects", "Associated Projects", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("users", "Consultancy Users", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("client_users", "Client Users", BUILDING_VIEW_BANDS.MORE, true, BUILDING_VIEW_BANDS.BASIC),
    field("gauge_chart", "Gauge Chart", BUILDING_VIEW_BANDS.MORE),

    field("assessment_date", "Assessment Date", BUILDING_VIEW_BANDS.ASSESSMENT),
    field("redline_completed", "Redline Completed", BUILDING_VIEW_BANDS.ASSESSMENT),
    field("redline_completed_date", "Redline Completed Date", BUILDING_VIEW_BANDS.ASSESSMENT),
    field("continuous_fca_completed", "Continuous FCA Completed", BUILDING_VIEW_BANDS.ASSESSMENT),
    field("continuous_fca_completed_date", "Continuous FCA Completed Date", BUILDING_VIEW_BANDS.ASSESSMENT),

    field("place", "Location", BUILDING_VIEW_BANDS.ADDRESS),
    field("street", "Street", BUILDING_VIEW_BANDS.ADDRESS),
    field("street_address_2", "Street Address 2", BUILDING_VIEW_BANDS.ADDRESS),
    field("city", "City", BUILDING_VIEW_BANDS.ADDRESS),
    field("state", "State", BUILDING_VIEW_BANDS.ADDRESS),
    field("country", "Country", BUILDING_VIEW_BANDS.ADDRESS),
    field("zip_code", "Zip Code", BUILDING_VIEW_BANDS.ADDRESS),
    field("latitude", "Latitude", BUILDING_VIEW_BANDS.ADDRESS),
    field("longitude", "Longitude", BUILDING_VIEW_BANDS.ADDRESS),
    field("comments", "Comments", BUILDING_VIEW_BANDS.ADDRESS),
    field("created_at", "Created At", BUILDING_VIEW_BANDS.ADDRESS),
    field("updated_at", "Updated At", BUILDING_VIEW_BANDS.ADDRESS)
];

export const BUILDING_VIEW_FORM_FIELD_ORDER = [
    "code",
    "name",
    "main_building_type_id",
    "description",
    "consultancy_id",
    "client_id",
    "region_id",
    "site_id",
    "number",
    "projects",
    "users",
    "client_users",
    "area",
    "cost",
    "crv",
    "year",
    "original_age",
    "major_renovation_year",
    "major_renovation_age",
    "ownership_type",
    "enterprise_index",
    "ownership",
    "leases_count",
    "lease_end_date",
    "manager",
    "senior_property_manager",
    "use",
    "secondary_use",
    "sector",
    "internal_group",
    "division",
    "status",
    "criticality",
    "window_sf",
    "climate_zone",
    "sf_by_ton_standard",
    "sf_by_ton_actual",
    "hospital_name",
    "ministry",
    "color_code",
    "sort_order",
    "assessment_date",
    "redline_completed",
    "redline_completed_date",
    "continuous_fca_completed",
    "continuous_fca_completed_date",
    "place",
    "street",
    "street_address_2",
    "city",
    "state",
    "country",
    "zip_code",
    "latitude",
    "longitude",
    "comments",
    "created_at",
    "updated_at"
];

export const dynamicFieldConfigKey = fieldName => `df__${fieldName}`;

const isHiddenVisibility = value =>
    value === false || value === 0 || value === "0" || value === "false";

const resolveDynamicFieldVisibility = (current, saved, def) => {
    if (isHiddenVisibility(current) || isHiddenVisibility(saved)) return false;
    return current ?? saved ?? def?.isVisible;
};

export const buildBuildingViewFieldDefaults = () => {
    const config = {};
    const keys = [];
    STATIC_FIELDS.forEach(({ key, label, band, bandForm, isVisible }) => {
        keys.push(key);
        config[key] = { label, band, bandForm, isVisible };
    });
    return { keys, config };
};

export const buildingViewFieldDefaults = buildBuildingViewFieldDefaults();

export const getBuildingViewFieldBand = (key, fieldConfig, layoutContext = "view") => {
    const entry = fieldConfig?.config?.[key];
    if (!entry) return null;
    if (layoutContext === "form") {
        return entry.bandForm ?? entry.band;
    }
    return entry.band;
};

export const buildDynamicFieldEntries = (dynamicBands = {}) => {
    const entries = [];
    if (!dynamicBands || typeof dynamicBands !== "object") return entries;
    Object.entries(dynamicBands).forEach(([bandName, fields]) => {
        if (!Array.isArray(fields)) return;
        fields.forEach(fieldDef => {
            const fieldName = fieldDef?.field_name;
            if (!fieldName) return;
            const key = dynamicFieldConfigKey(fieldName);
            const bandKey = dynamicBandConfigKey(bandName);
            entries.push({
                key,
                label: fieldDef.label || fieldName,
                band: bandKey,
                bandForm: bandKey,
                bandDisplay: bandName,
                isVisible: fieldDef?.rules?.table_default_visible !== false
            });
        });
    });
    return entries;
};

export const mergeDynamicFieldsIntoDefaults = (defaults, dynamicBands = {}) => {
    const dynamicEntries = buildDynamicFieldEntries(dynamicBands);
    if (!dynamicEntries.length) return defaults;

    const keys = [...defaults.keys];
    const config = { ...defaults.config };
    const addressIdx = keys.indexOf("place");
    const insertAt = addressIdx >= 0 ? addressIdx : keys.length;

    let offset = 0;
    dynamicEntries.forEach(entry => {
        if (config[entry.key]) {
            config[entry.key] = {
                ...config[entry.key],
                label: entry.label,
                band: entry.band,
                bandForm: entry.bandForm ?? entry.band,
                bandDisplay: entry.bandDisplay,
                isDynamicField: true,
                isVisible: config[entry.key].isVisible ?? entry.isVisible
            };
            if (!keys.includes(entry.key)) {
                keys.splice(insertAt + offset, 0, entry.key);
                offset += 1;
            }
            return;
        }
        keys.splice(insertAt + offset, 0, entry.key);
        offset += 1;
        config[entry.key] = {
            label: entry.label,
            band: entry.band,
            bandForm: entry.bandForm ?? entry.band,
            bandDisplay: entry.bandDisplay,
            isVisible: entry.isVisible,
            isDynamicField: true
        };
    });

    return { keys, config };
};

export const alignBuildingViewFieldConfigOrder = (fieldConfig, dynamicBands = {}, layoutContext = "view") => {
    if (!fieldConfig?.config) return fieldConfig;
    const defaults = mergeDynamicFieldsIntoDefaults(buildBuildingViewFieldDefaults(), dynamicBands);
    const orderTemplate =
        layoutContext === "form"
            ? [...BUILDING_VIEW_FORM_FIELD_ORDER, ...defaults.keys.filter(k => !BUILDING_VIEW_FORM_FIELD_ORDER.includes(k))]
            : defaults.keys;
    const orderSet = new Set(orderTemplate);
    const defaultKeySet = new Set(defaults.keys);
    const rawPresentKeys = fieldConfig.keys || Object.keys(fieldConfig.config);
    const presentKeys = rawPresentKeys.filter(key => defaultKeySet.has(key) || !String(key).startsWith("df__"));
    const presentSet = new Set(presentKeys);

    const ordered = orderTemplate.filter(key => presentSet.has(key));
    presentKeys.forEach(key => {
        if (!orderSet.has(key) && !ordered.includes(key)) {
            const addressIdx = ordered.indexOf("place");
            const insertAt = addressIdx >= 0 ? addressIdx : ordered.length;
            ordered.splice(insertAt, 0, key);
        }
    });

    const savedConfig = fieldConfig.config;
    const config = Object.entries(savedConfig).reduce((acc, [key, value]) => {
        const isDynamic = String(key).startsWith("df__");
        if (!isDynamic || defaultKeySet.has(key) || isHiddenVisibility(value?.isVisible)) {
            acc[key] = value;
        }
        return acc;
    }, {});
    const insertBeforeAddress = key => {
        if (ordered.includes(key)) return;
        const addressIdx = ordered.indexOf("place");
        const insertAt = addressIdx >= 0 ? addressIdx : ordered.length;
        ordered.splice(insertAt, 0, key);
    };

    defaults.keys.forEach(key => {
        const def = defaults.config[key];
        if (!def) return;
        if (def.isDynamicField || String(key).startsWith("df__")) {
            const saved = savedConfig[key];
            if (!config[key]) {
                config[key] = saved
                    ? {
                          ...def,
                          ...saved,
                          label: def.label,
                          band: def.band,
                          bandForm: def.bandForm,
                          bandDisplay: def.bandDisplay,
                          isDynamicField: true,
                          isVisible: resolveDynamicFieldVisibility(undefined, saved?.isVisible, def.isVisible)
                      }
                    : { ...def };
            } else {
                config[key] = {
                    ...config[key],
                    label: def.label,
                    band: def.band,
                    bandForm: def.bandForm,
                    bandDisplay: def.bandDisplay,
                    isDynamicField: true,
                    isVisible: resolveDynamicFieldVisibility(config[key].isVisible, saved?.isVisible, def.isVisible)
                };
            }
            insertBeforeAddress(key);
            return;
        }
        if (config[key] && def) {
            config[key] = {
                ...config[key],
                label: def.label,
                band: def.band,
                bandForm: def.bandForm
            };
        }
    });

    return { keys: ordered, config };
};
